import { TabTarget } from "./tabTarget";

/**
 * Translates between a tab's target and the editor route it lives at.
 *
 * Both directions live here so they cannot drift. `locationToTarget` is what lets the URL be the
 * single source of truth for which tab is active — without it, route changes can only be observed
 * indirectly, by whichever component happens to render for that route.
 *
 * Paths are relative to the account slug; `useNavigateWithSlug` prepends it.
 */

/** Search params the editor routes carry. Values arrive loosely typed from the URL. */
export type EditorSearch = {
  dashboardId?: unknown;
  tileId?: unknown;
};

export function targetToPath(target: TabTarget): string {
  switch (target.kind) {
    case "model":
      return `/editor/${target.modelId}`;
    case "draft":
      return `/editor/draft/${target.draftId}`;
    case "dashboard":
      return `/editor/dashboard/${target.dashboardId}`;
    case "scratch":
      return "/editor";
  }
}

/**
 * The dashboard context a target owns. `undefined` entries clear the param — react-location drops
 * undefined values when it stringifies search, so a draft or dashboard route cannot inherit a stale
 * `dashboardId` from the model route it was opened from.
 */
export function targetToSearch(target: TabTarget): {
  dashboardId: string | undefined;
  tileId: string | undefined;
} {
  if (target.kind === "model") {
    return { dashboardId: target.dashboardId, tileId: target.tileId };
  }
  return { dashboardId: undefined, tileId: undefined };
}

export type TabLocation = {
  to: string;
  search: (
    prev: Record<string, unknown> | undefined,
  ) => Record<string, unknown>;
};

/** A navigate argument for `useNavigateWithSlug`, preserving unrelated search params. */
export function targetToLocation(target: TabTarget): TabLocation {
  const search = targetToSearch(target);
  return {
    to: targetToPath(target),
    search: (prev) => ({ ...prev, ...search }),
  };
}

const asId = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/**
 * The target a URL points at, or `null` if the path is not an editor route.
 *
 * `pathname` may or may not include the account slug — the editor segment is located by name rather
 * than by position so both forms parse.
 */
export function locationToTarget(
  pathname: string,
  search: EditorSearch = {},
): TabTarget | null {
  const segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);
  const editorIndex = segments.indexOf("editor");
  if (editorIndex === -1) return null;

  const rest = segments.slice(editorIndex + 1);

  if (rest.length === 0) return { kind: "scratch" };

  if (rest[0] === "draft") {
    const draftId = rest[1];
    return draftId ? { kind: "draft", draftId } : null;
  }

  if (rest[0] === "dashboard") {
    const dashboardId = rest[1];
    return dashboardId ? { kind: "dashboard", dashboardId } : null;
  }

  if (rest.length > 1) return null;

  return {
    kind: "model",
    modelId: rest[0],
    dashboardId: asId(search.dashboardId),
    tileId: asId(search.tileId),
  };
}
